/**
 * Permission system types
 */

export type PermissionValue = 1 | 0;

/**
 * Permission constants - centralized permission definitions
 */
export const PERMISSIONS = {
  // CSV permissions
  CSV_ACCESS: 'csv_access',
  CSV_READ_ALL: 'csv_readAll',
  CSV_UPLOAD: 'csv_upload',
  CSV_DELETE: 'csv_delete',
  CSV_APPROVE: 'csv_approve',

  // Whitelist permissions
  WHITELIST_ACCESS: 'whitelist_access',

  // File permissions
  FILES_ACCESS: 'files_access',
  FILES_READ_ALL: 'files_readAll',
  FILES_UPLOAD: 'files_upload',
  FILES_DELETE: 'files_delete',

  // Study Files permissions
  STUDY_FILES_ACCESS: 'studyFiles_access',
  STUDY_FILES_READ_ALL: 'studyFiles_readAll',
  STUDY_FILES_UPLOAD: 'studyFiles_upload',
  STUDY_FILES_DELETE: 'studyFiles_delete',

  // Audit Trail permissions
  AUDIT_TRAIL_ACCESS: 'auditTrail_access',

  // Study Data Import permissions
  DATA_ACCESS: 'data_access',


  // Other permissions
  ADMIN_STUDY: 'adminStudy',
  CAN_ACCESS_NAV_BAR: 'canAccessNavBar',
  CAN_ACCESS_SIDEBAR: 'canAccessSidebar',
  CAN_ACCESS_SIDE_PANEL: 'canAccessSidePanel',
  CAN_EPRO: 'canEPro',
  CAN_USE_RANDOMIZED_STATUS: 'canUseRandomizedStatus',
  CLOSE_QUERY: 'closeQuery',
  CLOSE_SITE_QUERY: 'closeSiteQuery',
  CLOSE_SYSTEM_QUERY: 'closeSystemQuery',
  CREATE_CONFIG: 'createConfig',
  CAN_CREATE_FORMS_SUMMARIES: 'canCreateFormsSummaries',
  CREATE_ECONSENT: 'createEConsent',
  CREATE_FORM_DATA: 'createFormData',
  CREATE_QUERY: 'createQuery',
  CREATE_RANDOMIZATION: 'createRandomization',
  CREATE_STUDY: 'createStudy',
  CREATE_SUBJECT: 'createSubject',
  CREATE_TEMPLATE: 'createTemplate',
  DATA_EXPORT: 'dataExport',
  DELETE_FORM: 'deleteForm',
  EDIT_REPORT: 'editReport',
  FREEZE_OFF: 'freezeOff',
  FREEZE_ON: 'freezeOn',
  IS_DEV: 'isDev',
  LOCK_OFF: 'lockOff',
  LOCK_ON: 'lockOn',
  MISSING_OFF: 'missingOff',
  MISSING_ON: 'missingOn',
  NA_OFF: 'naOff',
  NA_ON: 'naOn',
  OPEN_QUERY: 'openQuery',
  OPEN_SYSTEM_QUERY: 'openSystemQuery',
  PRINT: 'print',
  PRINT_BLANK_FORM: 'printBlankForm',
  READ_ECONSENT: 'readEConsent',
  READ_ENROLLMENT_BY_SITE: 'readEnrollmentBySite',
  READ_FORM_DATA: 'readFormData',
  READ_FORM_AUDIT_TRAIL: 'readFormAuditTrail',
  READ_QUERY: 'readQuery',
  READ_SITE: 'readSite',
  READ_SUBJECT: 'readSubject',
  READ_VAS_SCORES: 'readVASScores',
  REPORTING: 'reporting',
  RESTORE_FORM: 'restoreForm',
  SIGN_FORM: 'signForm',
  SIGN_OFF: 'signOff',
  SIGN_ON: 'signOn',
  SUBMIT_QPP: 'submitQPP',
  UPDATE_FORM_DATA: 'updateFormData',
  UPDATE_SUBJECT: 'updateSubject',
  VERIFY_OFF: 'verifyOff',
  VERIFY_ON: 'verifyOn',
  VIEW_DELETED_SUB_FORMS: 'viewDeletedSubForms',
} as const;

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS];

export interface Permissions {
  // New simplified permissions
  csv_access?: PermissionValue;
  csv_readAll?: PermissionValue;
  csv_upload?: PermissionValue;
  csv_delete?: PermissionValue;
  csv_approve?: PermissionValue;
  whitelist_access?: PermissionValue;
  files_access?: PermissionValue;
  files_readAll?: PermissionValue;
  files_upload?: PermissionValue;
  files_delete?: PermissionValue;
  studyFiles_access?: PermissionValue;
  studyFiles_readAll?: PermissionValue;
  studyFiles_upload?: PermissionValue;
  studyFiles_delete?: PermissionValue;
  auditTrail_access?: PermissionValue;
  data_access?: PermissionValue;


  // Other permissions (for future use)
  adminStudy?: PermissionValue;
  canAccessNavBar?: PermissionValue;
  canAccessSidebar?: PermissionValue;
  canAccessSidePanel?: PermissionValue;
  canEPro?: PermissionValue;
  canUseRandomizedStatus?: PermissionValue;
  closeQuery?: PermissionValue;
  closeSiteQuery?: PermissionValue;
  closeSystemQuery?: PermissionValue;
  createConfig?: PermissionValue;
  canCreateFormsSummaries?: PermissionValue;
  createEConsent?: PermissionValue;
  createFormData?: PermissionValue;
  createQuery?: PermissionValue;
  createRandomization?: PermissionValue;
  createStudy?: PermissionValue;
  createSubject?: PermissionValue;
  createTemplate?: PermissionValue;
  dataExport?: PermissionValue;
  deleteForm?: PermissionValue;
  editReport?: PermissionValue;
  freezeOff?: PermissionValue;
  freezeOn?: PermissionValue;
  isDev?: PermissionValue;
  lockOff?: PermissionValue;
  lockOn?: PermissionValue;
  missingOff?: PermissionValue;
  missingOn?: PermissionValue;
  naOff?: PermissionValue;
  naOn?: PermissionValue;
  openQuery?: PermissionValue;
  openSystemQuery?: PermissionValue;
  print?: PermissionValue;
  printBlankForm?: PermissionValue;
  readEConsent?: PermissionValue;
  readEnrollmentBySite?: PermissionValue;
  readFormData?: PermissionValue;
  readFormAuditTrail?: PermissionValue;
  readQuery?: PermissionValue;
  readSite?: PermissionValue;
  readSubject?: PermissionValue;
  readVASScores?: PermissionValue;
  reporting?: PermissionValue;
  restoreForm?: PermissionValue;
  signForm?: PermissionValue;
  signOff?: PermissionValue;
  signOn?: PermissionValue;
  submitQPP?: PermissionValue;
  updateFormData?: PermissionValue;
  updateSubject?: PermissionValue;
  verifyOff?: PermissionValue;
  verifyOn?: PermissionValue;
  viewDeletedSubForms?: PermissionValue;
}

export interface Study {
  id: number;
  name: string;
  description?: string;
  site_id?: number;
}

export interface Role {
  id: number;
  name: string;
  description: string;
  json_permissions: string;
  study_id: number;
}

/**
 * Route to permission mapping
 */
export const ROUTE_PERMISSIONS: Record<string, PermissionKey[]> = {
  '/file-upload': [PERMISSIONS.FILES_ACCESS],
  '/file-upload/files': [PERMISSIONS.FILES_ACCESS],
  '/data-import': [PERMISSIONS.DATA_ACCESS],
  '/csv-upload': [PERMISSIONS.CSV_ACCESS],
  '/audit-trail': [PERMISSIONS.AUDIT_TRAIL_ACCESS],
  '/study-files': [PERMISSIONS.STUDY_FILES_ACCESS],
  '/whitelist-management': [PERMISSIONS.WHITELIST_ACCESS],
};

/**
 * Parse JSON permissions string to Permissions object
 */
export function parsePermissions(jsonPermissions: string): Permissions {
  try {
    return JSON.parse(jsonPermissions) as Permissions;
  } catch (_error) {
    return {};
  }
}

/**
 * Check if user has any of the specified permissions
 */
export function hasAnyPermission(
  permissions: Permissions,
  requiredPermissions: PermissionKey[]
): boolean {
  return requiredPermissions.some(
    permission => permissions[permission as keyof Permissions] === 1
  );
}

/**
 * Check if user has all of the specified permissions
 */
export function hasAllPermissions(
  permissions: Permissions,
  requiredPermissions: PermissionKey[]
): boolean {
  return requiredPermissions.every(
    permission => permissions[permission as keyof Permissions] === 1
  );
}
